import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Building2, Search, Pencil, Trash2, Loader2, MapPin, Users, Bookmark, Sparkles, CalendarClock, SlidersHorizontal,
} from "lucide-react";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [companies, setCompanies] = useState(null);
  const [favCount, setFavCount] = useState(0);

  const load = async () => {
    const [c, f] = await Promise.all([api.get("/companies"), api.get("/favorites")]);
    setCompanies(c.data);
    setFavCount(f.data.length);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Azienda eliminata");
      load();
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const toggleAuto = async (company, enabled) => {
    setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, auto_search: enabled } : c)));
    try {
      await api.patch(`/companies/${company.id}/auto-search`, { enabled });
      toast.success(enabled ? "Ricerca automatica attivata" : "Ricerca automatica disattivata");
    } catch {
      toast.error("Impossibile aggiornare l'impostazione");
      load();
    }
  };

  const updateSettings = async (company, patch) => {
    setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, ...patch } : c)));
    try {
      await api.patch(`/companies/${company.id}/settings`, patch);
    } catch {
      toast.error("Impossibile aggiornare le impostazioni");
      load();
    }
  };

  if (companies === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow text-indigo-600">Le tue società</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">
            Aziende & Bandi
          </h1>
          <p className="mt-1 text-slate-500">Gestisci i profili aziendali e avvia la ricerca AI dei bandi.</p>
        </div>
        <Button asChild className="gap-2" data-testid="add-company-button">
          <Link to="/app/aziende/nuova">
            <Plus className="h-4 w-4" />
            Nuova Azienda
          </Link>
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Building2} label="Aziende" value={companies.length} />
        <StatCard icon={Bookmark} label="Bandi salvati" value={favCount} />
        <StatCard icon={Sparkles} label="Motore AI" value="Attivo" />
      </div>

      {companies.length === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="empty-companies">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <Building2 className="h-7 w-7 text-slate-400" />
          </span>
          <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Nessuna azienda ancora</h3>
          <p className="mt-1 max-w-sm text-sm text-slate-500">
            Crea il profilo della tua società per far cercare all'AI i bandi più adatti.
          </p>
          <Button asChild className="mt-6 gap-2">
            <Link to="/app/aziende/nuova">
              <Plus className="h-4 w-4" />
              Crea la prima azienda
            </Link>
          </Button>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {companies.map((c) => (
            <div key={c.id} data-testid="company-card" className="animate-in-up flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
              <div className="flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <Building2 className="h-5 w-5" />
                </span>
                <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] font-semibold text-slate-600">
                  {c.ateco}
                </span>
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{c.ragione_sociale}</h3>
              <div className="mt-2 space-y-1 text-sm text-slate-500">
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{c.regione}</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" />{c.dimensione} · {c.dipendenti} dip.</div>
              </div>
              {Array.isArray(c.obiettivi) && c.obiettivi.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.obiettivi.slice(0, 3).map((o) => (
                    <span key={o} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{o}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    <CalendarClock className="h-4 w-4 text-indigo-500" />
                    Ricerca automatica + email
                  </span>
                  <Switch
                    checked={c.auto_search !== false}
                    onCheckedChange={(v) => toggleAuto(c, v)}
                    data-testid="auto-search-toggle"
                  />
                </div>
                {c.auto_search !== false && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-400">Frequenza</span>
                      <Select
                        value={c.search_frequency || "settimanale"}
                        onValueChange={(v) => updateSettings(c, { search_frequency: v })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="frequency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="settimanale">Ogni settimana</SelectItem>
                          <SelectItem value="quindicinale">Ogni 2 settimane</SelectItem>
                          <SelectItem value="mensile">Ogni mese</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <span className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        <SlidersHorizontal className="h-3 w-3" />Compat. min.
                      </span>
                      <Select
                        value={String(c.min_compat ?? 0)}
                        onValueChange={(v) => updateSettings(c, { min_compat: parseInt(v, 10) })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="min-compat-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Tutti</SelectItem>
                          <SelectItem value="50">≥ 50%</SelectItem>
                          <SelectItem value="60">≥ 60%</SelectItem>
                          <SelectItem value="70">≥ 70%</SelectItem>
                          <SelectItem value="80">≥ 80%</SelectItem>
                          <SelectItem value="90">≥ 90%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-auto flex flex-wrap gap-2 pt-4">
                <Button asChild className="flex-1 gap-1.5" data-testid="search-grants-button">
                  <Link to={`/app/aziende/${c.id}/bandi`}>
                    <Search className="h-4 w-4" />
                    Cerca bandi
                  </Link>
                </Button>
                <Button asChild variant="outline" size="icon" data-testid="edit-company-button">
                  <Link to={`/app/aziende/${c.id}/modifica`}><Pencil className="h-4 w-4" /></Link>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon" data-testid="delete-company-button">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare questa azienda?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Verranno rimossi anche i bandi salvati per "{c.ragione_sociale}". L'azione è irreversibile.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(c.id)} data-testid="confirm-delete-company" className="bg-red-600 hover:bg-red-700">
                        Elimina
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
